import { Form, SelectInput } from '@ijoin/components';
import {
  useGetAdminQueue,
  useGetAdminQueueGateSamples,
  useGetAdminQueueSamples
} from '@ijoin/hooks';
import { Breadcrumbs, Stack, Typography } from '@mui/material';
import { keepPreviousData } from '@tanstack/react-query';
import { createFileRoute, Link, redirect } from '@tanstack/react-router';

import { useEffect, useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';

import {
  QueueBacklogSection,
  QueueDetailHeader,
  QueueGatingActivitySection,
  QueueTuningHints
} from '../../components';
import {
  bucketByMinute,
  bucketMissesByReason,
  buildSummaryRows,
  groupQueueSamples,
  groupSamples,
  RANGE_OPTIONS
} from '../../components/queues.utils';

const ALL_INTEGRATIONS = 'all';

const QueueDetailPage = () => {
  const params = Route.useParams();

  const filterMethods = useForm({
    defaultValues: { hours: '6', integrationId: ALL_INTEGRATIONS }
  });
  const hoursValue = filterMethods.watch('hours');
  const integrationId = filterMethods.watch('integrationId');
  const hours = Number(hoursValue);

  const queue = useGetAdminQueue(params.name, {
    query: { refetchInterval: 60_000 }
  });

  const [from, setFrom] = useState('');

  useEffect(() => {
    setFrom(new Date(Date.now() - hours * 60 * 60 * 1000).toISOString());
  }, [hours]);

  const samples = useGetAdminQueueGateSamples(
    params.name,
    { from },
    {
      query: {
        enabled: !!from,
        placeholderData: keepPreviousData,
        refetchInterval: 60_000
      }
    }
  );

  const queueSamples = useGetAdminQueueSamples(
    params.name,
    { from },
    {
      query: {
        enabled: !!from,
        placeholderData: keepPreviousData,
        refetchInterval: 60_000
      }
    }
  );

  const integrationOptions = useMemo(() => {
    const map = new Map<string, string>();

    for (const s of samples.data?.data ?? []) {
      if (!map.has(s.integrationId)) {
        map.set(s.integrationId, s.integrationName ?? s.integrationId);
      }
    }

    return [
      { label: 'All integrations', value: ALL_INTEGRATIONS },
      ...[...map.entries()]
        .map(([id, name]) => ({ label: name, value: id }))
        .sort((a, b) => a.label.localeCompare(b.label))
    ];
  }, [samples.data]);

  const rangeOptions = useMemo(
    () => RANGE_OPTIONS.map(o => ({ label: o.label, value: String(o.hours) })),
    []
  );

  const filteredSamples = useMemo(() => {
    const all = samples.data?.data ?? [];

    return integrationId === ALL_INTEGRATIONS
      ? all
      : all.filter(s => s.integrationId === integrationId);
  }, [samples.data, integrationId]);

  const grouped = useMemo(
    () => groupSamples(filteredSamples),
    [filteredSamples]
  );
  const defers = useMemo(
    () => bucketByMinute(filteredSamples, 'defers', 'defers'),
    [filteredSamples]
  );
  const hits = useMemo(
    () => bucketByMinute(filteredSamples, 'hits', 'hits'),
    [filteredSamples]
  );
  const missesByReason = useMemo(
    () => bucketMissesByReason(filteredSamples),
    [filteredSamples]
  );
  const backlog = useMemo(
    () => groupQueueSamples(queueSamples.data?.data ?? []),
    [queueSamples.data]
  );
  const summaryRows = useMemo(
    () => buildSummaryRows(grouped.series),
    [grouped.series]
  );

  return (
    <Stack spacing={2} sx={{ p: 2 }}>
      <Breadcrumbs>
        <Link to='/queues'>SQS Queues</Link>
        <Typography color='text.primary'>{params.name}</Typography>
      </Breadcrumbs>

      {queue.data && <QueueDetailHeader queue={queue.data} />}

      {queue.data && <QueueTuningHints queue={queue.data} />}

      <Form methods={filterMethods}>
        <Stack
          alignItems='flex-end'
          direction='row'
          justifyContent='flex-end'
          spacing={2}
          sx={{ mt: 2 }}>
          <Stack sx={{ width: 280 }}>
            <SelectInput
              hideError
              label='Integration'
              name='integrationId'
              options={integrationOptions}
              size='small'
              variant='outlined'
            />
          </Stack>
          <Stack sx={{ width: 200 }}>
            <SelectInput
              hideError
              label='Time range'
              name='hours'
              options={rangeOptions}
              size='small'
              variant='outlined'
            />
          </Stack>
        </Stack>
      </Form>

      <QueueBacklogSection
        backlog={backlog}
        isLoading={queueSamples.isLoading}
      />

      <QueueGatingActivitySection
        defers={defers}
        grouped={grouped}
        hits={hits}
        isLoading={samples.isLoading}
        missesByReason={missesByReason}
        summaryRows={summaryRows}
      />
    </Stack>
  );
};

export const Route = createFileRoute('/queues/$name')({
  beforeLoad: () => {
    if (localStorage.getItem('isAdmin') !== '1') {
      throw redirect({ to: '/organizations' });
    }
  },
  component: QueueDetailPage
});
